import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const grantResponseSchema = z.object({
  granted: z.boolean(),
  grantedAt: z.string().nullable(),
  scopesPlainEnglish: z.array(z.string()),
  /**
   * Agent identity surface for the consent modal. Returned even when no
   * grant exists yet so the install prompt can show who's asking.
   */
  agent: z.object({
    displayName: z.string(),
    publisher: z.string(),
    trustLevel: z.enum(['first-party', 'third-party']),
    logoUrl: z.string().nullable(),
  }),
});

const installResponseSchema = z.object({
  installed: z.literal(true),
});

const runResponseSchema = z.object({
  outcome: z.enum(['extracted', 'unsupported', 'empty', 'blocked']),
  observationIds: z.array(z.string()),
  factCount: z.number(),
  diagnosticReportId: z.string().nullable(),
});

/**
 * Path-B (approval) request. The frontend sends *intent* — which report,
 * approve or reject, and any value edits the reviewer made — never the
 * provenance content. The backend composes the attestation Provenance and the
 * status transition server-side, attributed to the authenticated user.
 */
const attestBodySchema = z.object({
  diagnosticReportId: z.string(),
  decision: z.enum(['approve', 'reject']),
  edits: z
    .array(
      z.object({
        observationId: z.string(),
        /** Reviewer-corrected Observation name → written to `code.text`. */
        code: z.string().nullish(),
        valueQuantity: z
          .object({ value: z.number(), unit: z.string() })
          .nullish(),
        valueString: z.string().nullish(),
        referenceRange: z.string().nullish(),
        effectiveDateTime: z.string().nullish(),
      }),
    )
    .optional(),
});

const attestResponseSchema = z.object({
  diagnosticReportId: z.string(),
  status: z.enum(['final', 'entered-in-error']),
  observationIds: z.array(z.string()),
});

/** One pending review item shown in a mini-app's review surface — the wire
 *  twin of the framework's `PendingReviewItem` (any agent's items travel
 *  through this one shape; "finding" in the endpoint name is the framework's
 *  generic term, unrelated to the clinical-findings app). */
const pendingReviewItemSchema = z.object({
  resourceType: z.string(),
  id: z.string(),
  label: z.string(),
  display: z.string(),
  detail: z.string().nullable(),
  /** Verbatim supporting passage from the note/transcript, or null. */
  evidence: z.string().nullable(),
  /** Agent's 0–1 confidence; the list is returned sorted high→low. */
  confidence: z.number().nullable(),
  /** Agent-specific slice of the finding, carried opaquely — typed at both
   *  ends by the owning agent's schema (clinical-tasks:
   *  `clinicalTaskFindingExtrasSchema` in `@eluve/clinical-tasks`). */
  extras: z.record(z.string(), z.unknown()).optional(),
});

const findingsResponseSchema = z.object({
  findings: z.array(pendingReviewItemSchema),
});

/** A reviewer's edit to one pending finding's coding (display + primary code). */
const editFindingBodySchema = z.object({
  appointmentId: z.string(),
  resourceType: z.string(),
  id: z.string(),
  display: z.string().min(1),
  /** Coding system URL for the primary code, or null to drop the coding. */
  system: z.string().nullable(),
  /** Code value, or null to drop the coding. */
  code: z.string().nullable(),
  /** Agent-specific edit payload, carried opaquely — the owning agent
   *  validates it server-side against its own schema (clinical-tasks:
   *  `clinicalTaskEditExtrasSchema` in `@eluve/clinical-tasks`). */
  extras: z.record(z.string(), z.unknown()).optional(),
});

/** One match from the validated medical-code set, for the edit code picker. */
const codeSearchResultSchema = z.object({
  /** FHIR coding system URL (e.g. ICD-10-CM). */
  system: z.string(),
  code: z.string(),
  /** The code's canonical description. */
  display: z.string(),
});

const codeSearchResponseSchema = z.object({
  codes: z.array(codeSearchResultSchema),
});

const reviewBodySchema = z.object({
  appointmentId: z.string(),
  decisions: z.array(
    z.object({
      resourceType: z.string(),
      id: z.string(),
      decision: z.enum(['approve', 'reject']),
    }),
  ),
});

const reviewResponseSchema = z.object({
  approved: z.number(),
  rejected: z.number(),
});

const errorResponseSchema = z.object({
  error: z.string(),
});

/**
 * Mini-apps (SMART agents). For the initial pilot only one agent exists —
 * `extract-document`. The contract paths take an `:agentId` so adding the
 * second agent is a registry entry rather than a new HTTP surface.
 */
export const miniAppsContract = c.router(
  {
    grant: {
      method: 'GET',
      path: '/:agentId/grant',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      responses: {
        200: grantResponseSchema,
      },
      summary:
        'Check whether the current user has an active grant for an agent',
    },
    install: {
      method: 'POST',
      path: '/:agentId/install',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: installResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        'Provision a per-user ProjectMembership for the agent and exchange the caller-session JWT for agent-scoped Medplum tokens. Stores the resulting grant.',
    },
    run: {
      method: 'POST',
      path: '/:agentId/run',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      // Generic input bag — each agent declares its own zod inputSchema in
      // the backend registry and the input is validated there per agent.
      body: z.record(z.string(), z.unknown()),
      responses: {
        200: runResponseSchema,
        400: errorResponseSchema,
        403: errorResponseSchema,
        // A run for the same concurrency key (e.g. the same document) is
        // already in flight.
        409: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Run the agent's action against the agent-specific input, using the grant's agent-scoped Medplum tokens.",
    },
    attest: {
      method: 'POST',
      path: '/:agentId/attest',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      body: attestBodySchema,
      responses: {
        200: attestResponseSchema,
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        'Approve or reject an agent-extracted DiagnosticReport. The server flips the report + its Observations to final/entered-in-error and writes the attestation Provenance, attributed to the authenticated reviewer (path B).',
    },
    findings: {
      method: 'GET',
      path: '/:agentId/findings',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      query: z.object({ appointmentId: z.string() }),
      responses: {
        200: findingsResponseSchema,
        403: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "List an agent's pending (awaiting-review) findings for a session, read through the reviewer's own Medplum identity.",
    },
    review: {
      method: 'POST',
      path: '/:agentId/review',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      body: reviewBodySchema,
      responses: {
        200: reviewResponseSchema,
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Apply a reviewer's approve/reject decisions to an agent's pending findings (per-type confirmed/entered-in-error transition + attestation Provenance).",
    },
    editFinding: {
      method: 'POST',
      path: '/:agentId/findings/edit',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      body: editFindingBodySchema,
      responses: {
        200: z.object({ edited: z.literal(true) }),
        400: errorResponseSchema,
        403: errorResponseSchema,
        404: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        "Edit one of an agent's pending findings (reviewer-corrected display + code), written through the reviewer's own Medplum identity. The finding stays pending.",
    },
    searchCodes: {
      method: 'GET',
      path: '/:agentId/codes',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      query: z.object({
        /** Free text — matched against both code and description. */
        searchTerm: z.string().min(1),
        /** FHIR coding-system URL to scope to; defaults to ICD-10-CM (the only
         *  validated set today). Systems with no loaded codes return []. */
        system: z.string().optional(),
        limit: z.coerce.number().int().min(1).max(50).optional(),
      }),
      responses: {
        200: codeSearchResponseSchema,
        403: errorResponseSchema,
        422: errorResponseSchema,
      },
      summary:
        'Search the validated medical-code set (Eluve ICD-10) by name or code, to drive the finding-edit code picker. Only codes that exist here can be assigned.',
    },
    revoke: {
      method: 'DELETE',
      path: '/:agentId/grant',
      pathParams: z.object({
        tenantId: z.string(),
        agentId: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({ revoked: z.literal(true) }),
      },
      summary:
        "Revoke the caller's grant for an agent. Hard-deletes the grant row + the per-user agent ProjectMembership so the next install starts clean.",
    },
  },
  { pathPrefix: '/:tenantId/mini-apps' },
);
